import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Wand2, Users2, TrendingUp, ArrowRight } from "lucide-react";
import { cn } from "../../lib/utils";
import { cardMotion, scrollReveal } from "./motionConstants";

const cards = [
  {
    key: "browse_and_try",
    icon: Search,
    route: "/marketplace",
    accent: "text-primary",
    bg: "bg-primary/[0.06]",
    hover: "hover:border-primary/20",
  },
  {
    key: "create_your_own",
    icon: Wand2,
    route: "/portal?tab=activities",
    accent: "text-emerald-500",
    bg: "bg-emerald-500/[0.06]",
    hover: "hover:border-emerald-200",
  },
  {
    key: "build_your_identity",
    icon: Users2,
    route: "/spaces",
    accent: "text-indigo-500",
    bg: "bg-indigo-500/[0.06]",
    hover: "hover:border-indigo-200",
  },
  {
    key: "analyze_your_journey",
    icon: TrendingUp,
    route: "/portal?tab=my_workspace",
    accent: "text-amber-500",
    bg: "bg-amber-500/[0.06]",
    hover: "hover:border-amber-200",
  },
];

const DiscoverSection = ({
  variant = "tiles",
}: { variant?: "tiles" | "pills" } = {}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (variant === "pills") {
    return (
      <section aria-labelledby="explore-footer-title" className="flex flex-wrap items-center gap-2">
        <h2 id="explore-footer-title" className="w-full text-sm font-semibold text-foreground/80 tracking-tight">
          {t("what_can_you_do")}
        </h2>
        {cards.map((card) => (
          <button
            key={card.key}
            onClick={() => navigate(card.route)}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-card px-3.5 min-h-[44px] text-[12.5px] text-foreground/80 hover:border-primary/25 hover:text-foreground transition-colors"
            )}
          >
            <card.icon className={cn("h-3.5 w-3.5", card.accent)} />
            {t(`explore_pill_${card.key}`)}
          </button>
        ))}
      </section>
    );
  }

  return (
    <motion.section {...scrollReveal} className="space-y-3">
      <h2 className="text-base font-semibold text-foreground/80">
        {t("what_can_you_do")}
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {cards.map((card) => (
          <motion.button
            key={card.key}
            {...cardMotion}
            onClick={() => navigate(card.route)}
            className={cn(
              "group flex items-start gap-4 rounded-2xl border border-border/50 bg-card/60 backdrop-blur-sm",
              "p-5 text-start transition-shadow duration-300",
              "hover:shadow-lg hover:shadow-black/[0.04]",
              card.hover
            )}
          >
            <div
              className={cn(
                "flex-shrink-0 h-10 w-10 rounded-xl flex items-center justify-center transition-transform duration-300 group-hover:scale-105",
                card.bg
              )}
            >
              <card.icon className={cn("h-5 w-5", card.accent)} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                {t(card.key)}
                <ArrowRight className="w-3.5 h-3.5 opacity-0 -translate-x-1 rtl:translate-x-1 rtl:rotate-180 transition-all duration-300 group-hover:opacity-50 group-hover:translate-x-0" />
              </div>
              <div className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                {t(`${card.key}_description`)}
              </div>
            </div>
          </motion.button>
        ))}
      </div>
    </motion.section>
  );
};

export default DiscoverSection;
